<template>
  <el-dialog
    :visible.sync="visible"
    width="400px"
    :close-on-click-modal="false"
    :modal="true"
    :lock-scroll="true"
    custom-class="auth-dialog"
    @close="$emit('close')"
  >
    <div class="auth-form" @mousedown="onModalMouseDown" @click="onModalClick">
      <!-- Welcome Header -->
      <div class="welcome-header">
        <div class="welcome-details">
          <h4>Welcome to InterviewSignal Pro</h4>
          <div class="feature-highlight">
            <span class="feature-list">Live Interview Help • AI Interview Practice • Latest AI</span>
          </div>
        </div>
      </div>

      <!-- Sign In Form -->
      <div class="form-group">
        <el-input 
          v-model="loginForm.email" 
          placeholder="Enter your email"
          type="email"
          class="auth-input"
        />
      </div>
      <div class="form-group">
        <el-input 
          v-model="loginForm.password" 
          type="password" 
          placeholder="Enter your password"
          show-password
          class="auth-input"
        />
      </div>
      
      <!-- Status Messages -->
      <div v-if="statusMessage" class="status-message" :class="statusMessage.type">
        <i :class="statusMessage.type === 'success' ? 'el-icon-check' : 'el-icon-warning'"></i>
        <div class="message-content">
          <div class="message-title">{{ statusMessage.title }}</div>
          <div class="message-text">{{ statusMessage.text }}</div>
        </div>
      </div>
      
      <!-- Submit Button -->
      <el-button 
        type="primary" 
        @click="handleLogin" 
        :loading="loading"
        :disabled="loading"
        class="auth-button"
      >
        Sign In
      </el-button>
      
      <!-- Toggle to Sign Up -->
      <div class="auth-toggle">
        <span>Don't have an account?</span>
        <el-button type="text" @click="toggleMode" class="toggle-button">
          Sign Up
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { authHelpers } from '@/utils/supabase'

export default {
  name: 'AuthModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialMode: {
      type: String,
      default: 'login', // 'login' or 'signup'
      validator: (value) => ['login', 'signup'].includes(value)
    }
  },
  data() {
    return {
      loading: false,
      statusMessage: null,
      mouseDownInsideModal: false,
      loginForm: {
        email: '',
        password: ''
      }
    }
  },
  computed: {
    isSupabaseConfigured() {
      // Use the authHelpers to check if Supabase is properly configured
      return authHelpers.isConfigured()
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.clearForms()
        this.statusMessage = null
        // Prevent body scroll when modal opens
        document.body.style.overflow = 'hidden'
      } else {
        // Restore body scroll when modal closes
        document.body.style.overflow = ''
      }
    }
  },
  mounted() {
    // Add click listener to modal wrapper to detect outside clicks
    this.$nextTick(() => {
      const modalWrapper = document.querySelector('.el-dialog__wrapper')
      if (modalWrapper) {
        modalWrapper.addEventListener('click', this.onWrapperClick)
      }
    })
  },
  beforeDestroy() {
    // Clean up event listener
    const modalWrapper = document.querySelector('.el-dialog__wrapper')
    if (modalWrapper) {
      modalWrapper.removeEventListener('click', this.onWrapperClick)
    }
    // Ensure body scroll is restored when component is destroyed
    document.body.style.overflow = ''
  },
  methods: {
    onModalMouseDown() {
      this.mouseDownInsideModal = true
    },
    
    onModalClick(event) {
      // Prevent click from bubbling up to wrapper
      event.stopPropagation()
    },
    
    onWrapperClick(event) {
      // Only close if the mousedown didn't start inside the modal
      if (!this.mouseDownInsideModal && event.target.classList.contains('el-dialog__wrapper')) {
        this.$emit('close')
      }
      // Reset the flag after each click
      this.mouseDownInsideModal = false
    },
    toggleMode() {
      // Close login modal and open signup modal
      this.$emit('close')
      this.$emit('open-signup')
    },
    
    clearForms() {
      this.loginForm = {
        email: '',
        password: ''
      }
    },
    
    async handleLogin() {
      // Check if Supabase is configured first
      if (!this.isSupabaseConfigured) {
        this.statusMessage = {
          type: 'error',
          title: 'Service Unavailable',
          text: 'Authentication service is not available. Please contact support.'
        }
        return
      }
      
      this.loading = true
      this.statusMessage = null
      
      try {
        await this.handleSignIn()
      } catch (error) {
        this.statusMessage = {
          type: 'error',
          title: 'Login Error',
          text: error.message
        }
      } finally {
        this.loading = false
      }
    },
    
    async handleSignIn() {
      const { email, password } = this.loginForm
      
      if (!email || !password) {
        throw new Error('Please enter both email and password')
      }

      // Test connection first if this is a network error
      try {
        const connectionTest = await authHelpers.testConnection()
        console.log('🔍 Connection test result:', connectionTest)
        
        if (!connectionTest.fetchOk) {
          throw new Error(`Network connection failed (Status: ${connectionTest.fetchStatus}). Please check your internet connection.`)
        }
        
        if (!connectionTest.authOk) {
          throw new Error(`Authentication service error: ${connectionTest.authError}`)
        }
      } catch (testError) {
        console.error('🚨 Connection test failed:', testError)
        throw new Error('Unable to connect to authentication service. Please check your internet connection and try again.')
      }
      
      const { data, error } = await authHelpers.signIn(email, password)
      
      if (error) {
        console.error('Sign in error:', error)
        
        // Handle specific error cases
        if (error.message.includes('fetch') || error.message.includes('Network')) {
          throw new Error('Network connection failed. Please check your internet connection and try again.')
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error('Please verify your email address before signing in. Check your inbox for the verification link.')
        } else if (error.message.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password. Please check your credentials and try again.')
        } else if (error.message.includes('Too many requests')) {
          throw new Error('Too many login attempts. Please wait a moment and try again.')
        } else {
          throw new Error(error.message || 'Login failed. Please try again.')
        }
      }
      
      if (!data || !data.user) {
        throw new Error('Login failed. Please check your credentials.')
      }
      
      console.log('Successfully signed in user:', data.user.email)
      
      // Success - emit the auth event
      this.$emit('auth-success', data.user)
      this.clearForms()
    }
  }
}
</script>

<style scoped>
.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.welcome-header {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 8px;
  text-align: center;
}

.welcome-details h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

.feature-highlight {
  display: flex;
  flex-direction: column;
}

.feature-list {
  font-size: 13px;
  color: #4a5568;
  font-weight: 500;
  line-height: 1.4;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auth-input .el-input__inner {
  height: 44px;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  padding: 0 14px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: #f8fafc;
}

.auth-input .el-input__inner:focus {
  border-color: #0a66c2;
  box-shadow: 0 0 0 3px rgba(10, 102, 194, 0.1);
  background: white;
}

.status-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid;
}

.status-message.success {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #16a34a;
}

.status-message.error {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.message-content {
  flex: 1;
}

.message-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.message-text {
  font-size: 14px;
  line-height: 1.5;
}

.auth-button {
  height: 44px;
  background: linear-gradient(135deg, #0a66c2, #004182);
  border: none;
  font-weight: 600;
  font-size: 15px;
}

.auth-toggle {
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
}

.toggle-button {
  color: #0a66c2;
  font-weight: 600;
  padding: 0;
}

.toggle-button:hover {
  color: #004182;
}
</style>

<style scoped>
::v-deep .el-dialog {
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 5vh !important;
  margin-bottom: 5vh !important;
  max-height: 90vh;
  transform: none !important;
}

/* Remove gray header bar completely */
::v-deep .el-dialog__header {
  display: none;
}

/* Ensure dialog body starts at the top */
::v-deep .el-dialog__body {
  padding: 20px 20px 20px 20px;
  max-height: calc(90vh - 40px);
  overflow-y: auto;
  overflow-x: hidden;
}

/* Style the close button */
::v-deep .el-dialog__close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 18px;
  color: #909399;
  cursor: pointer;
  z-index: 10;
}

::v-deep .el-dialog__close:hover {
  color: #606266;
}

/* Prevent body scroll when modal is open */
::v-deep .el-dialog__wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Ensure modal overlay covers entire screen */
::v-deep .v-modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}
</style>